import React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import ExpandMoreSharpIcon from "@mui/icons-material/ExpandMoreSharp";
import RememberMeIcon from "@mui/icons-material/RememberMe";
import Pagination from "@mui/material/Pagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ReorderIcon from "@mui/icons-material/Reorder";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";
import Axios from "axios";
import { useEffect, useState, useContext } from "react";
import PlayerProfile from "./PlayerProfile";
import GlobalContext from "../GlobalContext";
import Moment from "react-moment";

import {
  Column,
  Table as ReactTable,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";

type PlayerGame = {
  playerID: string;
  gameID: {
    localTeamID: {
      _id: string;
      name: string;
    };
    guessTeamID: {
      _id: string;
      name: string;
    };
    date: Date;
    winner: string;
  };
  teamID: {
    _id: string;
  };
  ppg: number;
  fgm: number;
  pts: number;
  threes: number;
  twos: number;
  freeThrows: number;
  freeThrowsAttemps: number;
  fouls: number;
  win: Boolean;
};

const PlayerGamesList = ({ games }) => {
  const data = games;

  console.log(data);

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const columns = React.useMemo<ColumnDef<PlayerGame>[]>(
    () => [
      {
        header: "Info",
        columns: [
          {
            header: "Date",
            accessorFn: (row) => {
              return row.gameID.date;
            },

            cell: (info) => (
              <div className="flex flex-col">
                <Moment date={info.getValue()} format={"ddd"}></Moment>
                <Moment date={info.getValue()} format={"DD/MM/YYYY"}></Moment>
              </div>
            ),
            sortDescFirst: true,
          },
          {
            header: "Matchup",
            accessorFn: (row) =>
              `${row.gameID.localTeamID.name} ${row.gameID.guessTeamID.name}`,
            cell: (info) => (
              <div>
                <p>{info.row.original.gameID.localTeamID.name}</p>
                <p>vs.</p>
                <p>{info.row.original.gameID.guessTeamID.name}</p>
              </div>
            ),
            enableSorting: false,
            enableColumnFilter: false,
          },
          {
            header: "W/L",
            accessorFn: (row) => `${row.win}`,
            cell: (info) => (
              <div
                className={`${
                  info.getValue() === "true"
                    ? "text-green-500"
                    : info.getValue() === "false"
                    ? "text-red-500"
                    : ""
                } font-extrabold`}
              >
                {info.getValue() === "true"
                  ? "W"
                  : info.getValue() === "false"
                  ? "L"
                  : "TBD"}
              </div>
            ),
            enableSorting: false,
            enableColumnFilter: false,
          },
        ],
      },
      {
        header: "Stats",
        columns: [
          {
            header: "PTS",
            accessorKey: "pts",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
          },
          {
            header: "FGM",
            accessorKey: "fgm",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
          },
          {
            header: "3PM",
            accessorKey: "threes",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
          },
          {
            header: "FTM",
            accessorKey: "freeThrows",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
          },
          {
            header: "FTA",
            accessorKey: "freeThrowsAttempts",
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
          },
          {
            header: "FT%",
            accessorFn: (row) => `${row.freeThrows / row.freeThrowsAttemps}`,
            cell: (info) => (
              <span>{info.getValue() ? 0 : info.getValue()}%</span>
            ),
            enableColumnFilter: false,
            sortDescFirst: true,
          },
          {
            header: "Fouls",
            accessorFn: (row) => `${row.fouls}`,
            cell: (info) => <span>{info.getValue()}</span>,
            enableColumnFilter: false,
            sortDescFirst: true,
          },
        ],
      },
    ],
    []
  );

  function LocalTable({
    data,
    columns,
  }: {
    data: PlayerGame[];
    columns: ColumnDef<PlayerGame>[];
  }) {
    const table = useReactTable({
      data,
      columns,
      // Pipeline
      state: {
        sorting,
      },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      //
      debugTable: true,
    });

    return (
      <Box sx={{ width: "100%" }}>
        <TableContainer component={Paper} className="box">
          <div className="boxbox">
            <Table>
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableCell
                          key={header.id}
                          colSpan={header.colSpan}
                          sx={{ fontFamily: "Poppins", fontWeight: "bold" }}
                        >
                          {header.isPlaceholder ? null : (
                            <div>
                              <div
                                {...{
                                  className: header.column.getCanSort()
                                    ? "cursor-pointer select-none flex-col flex align-center items-center sortLabel"
                                    : "inline-block flex justify-center ",
                                  onClick:
                                    header.column.getToggleSortingHandler(),
                                }}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}{" "}
                                {header.column.getCanSort() &&
                                  header.column.getIsSorted() !== "asc" &&
                                  header.column.getIsSorted() !== "desc" && (
                                    <div className={`inline-block`}>
                                      <ExpandMoreSharpIcon
                                        className={` inline-block sort sortHover`}
                                      />
                                    </div>
                                  )}
                                {(header.column.getIsSorted() === "asc" ||
                                  header.column.getIsSorted() === "desc") && (
                                  <div className={`inline-block`}>
                                    <ExpandMoreSharpIcon
                                      className={` inline-block sort ${
                                        header.column.getIsSorted() === "asc"
                                          ? "sortFlip"
                                          : ""
                                      }`}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody id="tableBody">
                {table.getRowModel().rows.map((row) => {
                  return (
                    <TableRow key={row.id} id={row.getValue("_id")}>
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <TableCell
                            key={cell.id}
                            sx={{
                              textAlign: "center",
                              fontFamily: "Poppins",
                              padding: "4px",
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TableContainer>
      </Box>
    );
  }

  return (
    <>
      <LocalTable {...{ data, columns }} />
      <hr />
    </>
  );
};

export default PlayerGamesList;
